import { Anchor, List, Text } from "@mantine/core";
import {
  LegalContactRow,
  LegalPageLayout,
  LegalSection,
} from "./LegalPageLayout";

// Website-only Terms of Service (facts settled 2026-08-12: LivingSyncs,
// Inc., a Delaware corporation; Texas governing law; Montgomery County / S.D.
// Tex. venue). The application itself is sold under the separate customer
// agreements in the meta repo's docs/legal package (MSA or Product ToS + BAA).

export function TermsPage() {
  return (
    <LegalPageLayout
      title="Terms of"
      titleAccent="Service"
      metaDescription="Terms of Service for the LivingSyncs website. Use of the LivingSyncs platform itself is governed by separate customer agreements."
      lastUpdated="August 12, 2026"
    >
      <LegalSection heading="1. Acceptance of these terms">
        <Text c="dimmed">
          These Terms of Service govern your use of the public website operated
          by LivingSyncs, Inc., a Delaware corporation
          (&quot;LivingSyncs&quot;). By accessing or using this website, you
          agree to these terms. If you do not agree, please do not use the site.
        </Text>
      </LegalSection>

      <LegalSection heading="2. This website is informational">
        <Text c="dimmed">
          This website describes the LivingSyncs platform for group homes and
          assisted living facilities. Content on this site is provided for
          general information about our products and services. It is not legal,
          medical, or compliance advice, and it does not create any commitment
          to provide any feature or service.
        </Text>
      </LegalSection>

      <LegalSection heading="3. The LivingSyncs service is governed by separate agreements">
        <Text c="dimmed">
          Access to and use of the LivingSyncs application is provided under
          separate written agreements between LivingSyncs and each customer
          organization — including a subscription or master services agreement
          and, where protected health information is involved, a Business
          Associate Agreement. If there is any conflict between these website
          terms and a signed customer agreement, the customer agreement controls
          for use of the application.
        </Text>
      </LegalSection>

      <LegalSection heading="4. Demo requests and communications">
        <Text c="dimmed">
          Submitting a demo request or contact form does not create a contract
          or any obligation on either party. You agree to provide accurate
          contact information and not to include resident or patient health
          information in your messages. Our handling of information you submit
          is described in our <Anchor href="/privacy">Privacy Policy</Anchor>.
        </Text>
      </LegalSection>

      <LegalSection heading="5. Intellectual property">
        <Text c="dimmed">
          This website and its content — including text, graphics, logos, and
          software — are owned by LivingSyncs or its licensors and are protected
          by intellectual property laws. We grant you a limited, non-exclusive,
          non-transferable license to view the site for your personal or
          internal business evaluation. The LivingSyncs name and logo may not be
          used without our prior written permission.
        </Text>
      </LegalSection>

      <LegalSection heading="6. Acceptable use">
        <List c="dimmed" spacing="xs">
          <List.Item>
            Do not use the site in any way that violates applicable law.
          </List.Item>
          <List.Item>
            Do not attempt to gain unauthorized access to the site, our systems,
            or the LivingSyncs application.
          </List.Item>
          <List.Item>
            Do not scrape, harvest, or collect information from the site by
            automated means, or interfere with the site's operation.
          </List.Item>
          <List.Item>
            Do not misrepresent your identity or affiliation when contacting us.
          </List.Item>
        </List>
      </LegalSection>

      <LegalSection heading="7. Third-party links">
        <Text c="dimmed">
          The site may link to third-party websites or services we do not
          control. We are not responsible for their content or practices.
        </Text>
      </LegalSection>

      <LegalSection heading="8. Disclaimers">
        <Text c="dimmed">
          THE WEBSITE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot;
          WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING
          WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
          NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE WEBSITE WILL BE
          UNINTERRUPTED, ERROR-FREE, OR SECURE.
        </Text>
      </LegalSection>

      <LegalSection heading="9. Limitation of liability">
        <Text c="dimmed">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, LIVINGSYNCS WILL NOT BE LIABLE
          FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
          DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL, ARISING FROM OR
          RELATED TO YOUR USE OF THIS WEBSITE. OUR TOTAL LIABILITY FOR ANY CLAIM
          RELATING TO THE WEBSITE WILL NOT EXCEED ONE HUNDRED U.S. DOLLARS (US
          $100). LIABILITY FOR USE OF THE LIVINGSYNCS APPLICATION IS ADDRESSED
          IN THE APPLICABLE CUSTOMER AGREEMENT, NOT THESE WEBSITE TERMS.
        </Text>
      </LegalSection>

      <LegalSection heading="10. Governing law">
        <Text c="dimmed">
          These terms are governed by the laws of the State of Texas, without
          regard to its conflict-of-laws rules. Any dispute relating to these
          terms or the website will be brought exclusively in the state courts
          located in Montgomery County, Texas, or the United States District
          Court for the Southern District of Texas (Houston Division).
        </Text>
      </LegalSection>

      <LegalSection heading="11. Changes to these terms">
        <Text c="dimmed">
          We may update these terms from time to time. The updated version will
          be posted on this page with a revised &quot;Last updated&quot; date,
          and applies from the date it is posted.
        </Text>
      </LegalSection>

      <LegalSection heading="12. Contact">
        <LegalContactRow label="Questions about these terms">
          <Anchor href="mailto:hello@livingsyncs.com">
            hello@livingsyncs.com
          </Anchor>{" "}
          or (832) 991-3017
        </LegalContactRow>
        <LegalContactRow label="Legal notices">
          <Anchor href="mailto:legal@livingsyncs.com">
            legal@livingsyncs.com
          </Anchor>
        </LegalContactRow>
        <LegalContactRow label="Mailing address">
          LivingSyncs, Inc., P.O. Box 114, Splendora, TX 77372
        </LegalContactRow>
      </LegalSection>
    </LegalPageLayout>
  );
}
